import { useCallback, useEffect, useMemo, useState } from "react";
import { api, fetchBlob, getToken } from "../api/client";
import type { BoardItem, BoardsPayload, PassportDetail } from "../api/types";
import { useEventSource } from "../hooks/useEventSource";
import { formatDisplay } from "../lib/dates";
import { PassportDrawer, confirmDeletePassport, scheduleSaveBody, type ScheduleSave } from "../components/PassportDrawer";
import { AppleWalletMark } from "../components/AppleWalletMark";
import { useAuth } from "../auth/AuthContext";

function statusLabel(status: string, phase?: string) {
  if (status === "draft") return "Started";
  if (status === "locked") return phase === "preop" ? "In Pre-Op" : "Ready for Pre-Op";
  if (status === "preop_verified") return "Waiting for OR";
  if (status === "incision_verified") return "Incision";
  if (status === "cancelled") return "CANCELLED";
  return status.toUpperCase();
}

function statusClass(status: string) {
  if (status === "incision_verified") return "done";
  if (status === "preop_verified") return "or";
  if (status === "locked") return "preop";
  if (status === "draft") return "hold";
  return "";
}

function matchesQuery(item: BoardItem, q: string) {
  if (!q.trim()) return true;
  const needle = q.trim().toLowerCase();
  return [
    item.passport_code,
    item.patient_name,
    item.procedure,
    item.surgeon_name,
    item.or_suite,
    item.facility,
    item.patient_mrn,
    item.status,
  ].some((v) => String(v || "").toLowerCase().includes(needle));
}

function Card({ item, phase, onOpen }: { item: BoardItem; phase?: string; onOpen: (id: number) => void }) {
  const tone = statusClass(item.status);
  return (
    <button
      type="button"
      className={`card${tone ? ` ${tone}` : ""}${item.is_holdup ? " is-holdup" : ""}`}
      onClick={() => onOpen(item.id)}
    >
      <div className="code">
        {item.passport_code}
        <span className={`status-pill ${tone}`}>{statusLabel(item.status, phase)}</span>
        <AppleWalletMark show={item.in_apple_wallet} />
      </div>
      <div className="name">{item.patient_name}</div>
      <div className="proc">{item.procedure || "—"}</div>
      <div className="row">
        <span className="lat">{item.laterality}</span>
        <span>{[item.facility, item.or_suite].filter(Boolean).join(" · ") || "No suite"}</span>
      </div>
      <div className="row">
        <span>{item.surgeon_name}</span>
        <span>{formatDisplay(item.scheduled_or_at)}</span>
      </div>
      {item.schedule_variance_label && (
        <div className="row">
          <span className={`variance ${item.is_holdup ? "holdup" : ""}`}>{item.schedule_variance_label}</span>
        </div>
      )}
    </button>
  );
}

function Column({
  title,
  phase,
  items,
  query,
  onOpen,
}: {
  title: string;
  phase?: string;
  items: BoardItem[];
  query: string;
  onOpen: (id: number) => void;
}) {
  const filtered = items.filter((i) => matchesQuery(i, query));
  return (
    <section className="column" data-phase={phase || undefined}>
      <header>
        <h3>{title}</h3>
        <span className="badge">{filtered.length}</span>
      </header>
      <div className="list">
        {filtered.length
          ? filtered.map((item) => <Card key={item.id} item={item} phase={phase} onOpen={onOpen} />)
          : null}
      </div>
    </section>
  );
}

export function BoardPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [boards, setBoards] = useState<BoardsPayload | null>(null);
  const [query, setQuery] = useState("");
  const [error, setError] = useState("");
  const [detail, setDetail] = useState<PassportDetail | null>(null);
  const [printBusy, setPrintBusy] = useState("");
  const [archiveBusy, setArchiveBusy] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const token = getToken();
  const streamUrl = token ? `/api/portal/phases/stream?token=${encodeURIComponent(token)}` : null;

  const load = useCallback(async () => {
    const data = await api<BoardsPayload>("/api/portal/phases");
    setBoards(data);
    setError("");
  }, []);

  useEffect(() => {
    load().catch((e) => setError(e.message));
    const poll = window.setInterval(() => {
      load().catch(() => {});
    }, 15000);
    return () => clearInterval(poll);
  }, [load]);

  const live = useEventSource(streamUrl, "boards", (payload) => {
    try {
      setBoards(JSON.parse(payload) as BoardsPayload);
      setError("");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  });

  const waiting = useMemo(
    () => boards?.or_waiting || (boards?.or_phase || []).filter((p) => p.status === "preop_verified"),
    [boards],
  );
  const inOr = useMemo(
    () => boards?.or_in_or || (boards?.or_phase || []).filter((p) => p.status === "incision_verified"),
    [boards],
  );
  const holdups = boards?.holdup_count || 0;

  async function openPassport(id: number) {
    const data = await api<PassportDetail>(`/api/portal/passports/${id}`);
    setDetail(data);
  }

  async function saveSuite(id: number, fields: ScheduleSave) {
    const data = await api<PassportDetail>(`/api/portal/passports/${id}/suite`, {
      method: "PATCH",
      body: JSON.stringify(scheduleSaveBody(fields)),
    });
    setDetail(data);
    await load();
  }

  async function printDoc(kind: "consent" | "report" | "record") {
    if (!detail) return;
    setPrintBusy(kind);
    try {
      const path =
        kind === "consent"
          ? `/api/passports/${detail.id}/consent/pdf`
          : kind === "report"
            ? `/api/passports/${detail.id}/report/pdf`
            : `/api/passports/${detail.id}/record/pdf`;
      const meta = await api<{ download_url: string }>(path, { method: "POST" });
      const blob = await fetchBlob(meta.download_url);
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
    } finally {
      setPrintBusy("");
    }
  }

  async function archiveCase() {
    if (!detail) return;
    setArchiveBusy(true);
    try {
      const data = await api<PassportDetail>(`/api/portal/passports/${detail.id}/complete`, {
        method: "POST",
      });
      setDetail(data);
      await load();
    } finally {
      setArchiveBusy(false);
    }
  }

  async function removePassport() {
    if (!detail) return;
    setDeleteBusy(true);
    try {
      if (await confirmDeletePassport(detail.passport_code, detail.id)) {
        setDetail(null);
        await load();
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setDeleteBusy(false);
    }
  }

  return (
    <>
      <div className="board-view">
      <div className="toolbar">
        <div>
          <span className={`live-dot${live ? " on" : ""}`} />
          <strong>{live ? "Live" : "Connecting"}</strong>
          {" · today’s cases"}
          {holdups ? (
            <>
              {" · "}
              <span className="variance holdup">
                {holdups} holdup{holdups === 1 ? "" : "s"}
              </span>
            </>
          ) : (
            " · on schedule"
          )}
          {boards?.generated_at ? ` · ${formatDisplay(boards.generated_at)}` : ""}
        </div>
        <div className="toolbar-actions">
          <input
            type="search"
            placeholder="Search patient, code, MRN…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search board"
          />
          <button type="button" className="tool-btn" onClick={() => load().catch((e) => setError(e.message))}>
            Refresh
          </button>
        </div>
      </div>
      {error && <div className="banner-error">{error}</div>}
      <div className="boards">
        <Column title="Hold" phase="hold" items={boards?.initial || []} query={query} onOpen={openPassport} />
        <Column title="Pre-op" phase="preop" items={boards?.preop || []} query={query} onOpen={openPassport} />
        <Column title="Waiting" phase="waiting" items={waiting} query={query} onOpen={openPassport} />
        <Column title="In OR" phase="in-or" items={inOr} query={query} onOpen={openPassport} />
      </div>
      </div>
      {detail && (
        <PassportDrawer
          detail={detail}
          printBusy={printBusy}
          archiveBusy={archiveBusy}
          defaultSuite={detail.or_suite || ""}
          defaultSched={detail.scheduled_or_at || ""}
          defaultFacility={detail.facility || ""}
          defaultArrive={detail.facility_at || ""}
          onClose={() => setDetail(null)}
          onSave={saveSuite}
          onPrint={printDoc}
          onArchive={archiveCase}
          onDelete={isAdmin ? removePassport : undefined}
          deleteBusy={deleteBusy}
        />
      )}
    </>
  );
}
